// Door panel component for interacting with doors

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Door panel component
 * Displays options for interacting with a door/opening
 */
class DoorPanel extends BaseComponent {
  static properties = {
    door: { type: Object },
  };

  static styles = css`
    :host {
      display: block;
    }

    .door-panel {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 2000;
      background: rgba(26, 26, 26, 0.98);
      border: 2px solid #444;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
      min-width: 300px;
      padding: 20px;
    }

    .door-panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
    }

    .door-panel-title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: white;
    }

    .door-panel-close {
      background: transparent;
      border: none;
      color: #999;
      cursor: pointer;
      padding: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: all 0.2s;
    }

    .door-panel-close:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }

    .door-panel-actions {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .door-action-button {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid #444;
      border-radius: 8px;
      color: white;
      cursor: pointer;
      font-size: 15px;
      font-weight: 500;
      transition: all 0.2s;
    }

    .door-action-button:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: #666;
      transform: translateY(-1px);
    }

    .door-action-button.primary {
      background: rgba(59, 130, 246, 0.2);
      border-color: #3b82f6;
    }

    .door-action-button.primary:hover {
      background: rgba(59, 130, 246, 0.3);
      border-color: #60a5fa;
    }

    .door-action-button:active {
      transform: translateY(0);
    }

    .icon {
      width: 20px;
      height: 20px;
    }
  `;

  constructor() {
    super();
    /** @type {import('../../types/jsdoc-types.js').Opening|null} */
    this.door = null;
  }

  _handleClose() {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true }));
  }

  _handleUtilize() {
    if (this.door) {
      this.dispatchEvent(new CustomEvent('utilize', { detail: this.door, bubbles: true }));
    }
  }

  _handleSearch() {
    if (this.door) {
      this.dispatchEvent(new CustomEvent('search', { detail: this.door, bubbles: true }));
    }
  }

  _handleListen() {
    if (this.door) {
      this.dispatchEvent(new CustomEvent('listen', { detail: this.door, bubbles: true }));
    }
  }

  _renderXIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
      </svg>
    `;
  }

  _renderDoorOpenIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M13 4h3a2 2 0 0 1 2 2v14" />
        <path d="M2 20h3" />
        <path d="M13 20h9" />
        <path d="M10 12v.01" />
        <path d="M13 4.562v16.157a1 1 0 0 1-1.242.97L5 20V5.562a2 2 0 0 1 1.515-1.94l4-1A2 2 0 0 1 13 4.561Z" />
      </svg>
    `;
  }

  _renderDoorClosedIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M18 20V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14" />
        <path d="M2 20h20" />
        <path d="M14 12v.01" />
      </svg>
    `;
  }

  _renderSearchIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
      </svg>
    `;
  }

  _renderEarIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M6 8.5a6.5 6.5 0 1 1 13 0c0 6-6 6-6 10a3.5 3.5 0 1 1-7 0" />
        <path d="M15 8.5a2.5 2.5 0 0 0-5 0v1a2 2 0 1 1 0 4" />
      </svg>
    `;
  }

  render() {
    if (!this.door) {
      return html``;
    }

    const isOpen = this.door.isOpen;
    const title = isOpen ? 'Open Door' : 'Closed Door';

    return html`
      <div class="door-panel">
        <div class="door-panel-header">
          <h3 class="door-panel-title">${title}</h3>
          <button class="door-panel-close" @click="${this._handleClose}">${this._renderXIcon()}</button>
        </div>
        <div class="door-panel-actions">
          <button class="door-action-button primary" @click="${this._handleUtilize}">
            ${isOpen ? this._renderDoorClosedIcon() : this._renderDoorOpenIcon()}
            <span>${isOpen ? 'Close' : 'Open'}</span>
          </button>
          <button class="door-action-button" @click="${this._handleSearch}">
            ${this._renderSearchIcon()}
            <span>Search</span>
          </button>
          <button class="door-action-button" @click="${this._handleListen}">
            ${this._renderEarIcon()}
            <span>Listen</span>
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define('door-panel', DoorPanel);
